// 행사일·배송일·회수일 편집 — 상세 화면 전용.
//
// ⚠️ 날짜 계산의 출처는 서버(services/dates.compute_rental_dates)다. 여기서는 자동
//    계산값(auto_*)을 서버에서 받아 보여주기만 하고, 계산을 다시 하지 않는다.
// ⚠️ "수동 지정"은 서버가 판정한 *_manual 값을 그대로 쓴다(저장값 != 자동값).
// ⚠️ 행사일만 저장하면 서버가 배송·회수를 새 행사일 기준으로 다시 계산한다.
//    배송·회수를 함께 보내면 그 값을 그대로 존중한다.
import React, { useEffect, useState } from "react";

import { eventDateLabel } from "./format";
import "./rental.css";

const FIELDS = [
  // 라벨은 상품 유형에 따라 바뀐다(eventDateLabel) — 여기 값은 기본(행사형)이다.
  { key: "event_date", label: "행사일", autoKey: "auto_event_date", manualKey: "event_date_manual" },
  {
    key: "delivery_date",
    label: "배송일",
    autoKey: "auto_delivery_date",
    manualKey: "delivery_date_manual",
  },
  {
    key: "pickup_date",
    label: "회수일",
    autoKey: "auto_pickup_date",
    manualKey: "pickup_date_manual",
  },
];

function pick(order) {
  return {
    event_date: order.event_date || "",
    delivery_date: order.delivery_date || "",
    pickup_date: order.pickup_date || "",
  };
}

export default function DateEditor({ order, busy, onSave }) {
  const [draft, setDraft] = useState(pick(order));
  const [dirty, setDirty] = useState(false);

  // 서버 응답이 갱신되면(저장·재동기화) 입력값을 맞춘다.
  // ⚠️ 의존성은 날짜 3개만 둔다 — order 객체 전체를 넣으면 관계없는 재렌더에도
  //    입력 중이던 값이 초기화된다. 그래서 값들을 먼저 꺼내 쓴다(eslint 만족).
  const { event_date: eventDate, delivery_date: deliveryDate, pickup_date: pickupDate } = order;
  useEffect(() => {
    setDraft({
      event_date: eventDate || "",
      delivery_date: deliveryDate || "",
      pickup_date: pickupDate || "",
    });
    setDirty(false);
  }, [eventDate, deliveryDate, pickupDate]);

  const change = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const save = () => {
    // 행사일만 바뀌었으면 배송·회수는 보내지 않는다 — 서버가 다시 계산하게 둔다.
    const base = pick(order);
    const payload = {};
    FIELDS.forEach(({ key }) => {
      if (draft[key] !== base[key]) payload[key] = draft[key] || null;
    });
    const onlyEvent =
      Object.keys(payload).length === 1 && Object.keys(payload)[0] === "event_date";
    onSave(payload, { onlyEvent });
  };

  const eventLabel = eventDateLabel(order);

  const resetToAuto = (field) => {
    const auto = order[field.autoKey];
    change(field.key, auto || "");
  };

  return (
    <div className="rental-section" style={{ marginBottom: 14 }}>
      <h3 className="rental-section-title">일정 ({eventLabel}·배송일·회수일)</h3>

      {FIELDS.map((field) => {
        const auto = order[field.autoKey];
        const manual = order[field.manualKey];
        const differsFromAuto = Boolean(auto) && draft[field.key] !== (auto || "");
        return (
          <div className="rental-row" key={field.key}>
            <span className="rental-row-label">
              {field.key === "event_date" ? eventLabel : field.label}
              {manual && <span className="rental-manual-badge">수동 지정</span>}
            </span>
            <input
              type="date"
              className="rental-date-input"
              value={draft[field.key]}
              onChange={(e) => change(field.key, e.target.value)}
            />
            {auto ? (
              <span className="rental-auto-hint">
                자동 {auto}
                {differsFromAuto && (
                  <button
                    type="button"
                    className="rental-btn-auto"
                    onClick={() => resetToAuto(field)}
                    title="자동 계산값으로 되돌립니다"
                  >
                    되돌리기
                  </button>
                )}
              </span>
            ) : (
              <span className="rental-auto-hint">자동값 없음</span>
            )}
          </div>
        );
      })}

      <div className="rental-row">
        <span className="rental-row-label">{eventLabel} 출처</span>
        <span className="rental-row-value">{order.event_date_source || "미확인"}</span>
      </div>

      <div className="rental-modal-actions" style={{ marginTop: 6 }}>
        <span className="rental-synced">
          {eventLabel}만 고쳐 저장하면 배송·회수가 자동 계산됩니다(공휴일 반영).
        </span>
        <button
          type="button"
          className="rental-btn-save"
          onClick={save}
          disabled={busy || !dirty}
        >
          저장
        </button>
      </div>
    </div>
  );
}
